import { usePortalGetPortalTransactions } from '@/lib/api/portal/portal';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';

export default function PortalTransactionsPage() {
  const { data, isLoading, isError, error } = usePortalGetPortalTransactions({ limit: 25 });

  return (
    <section className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Transactions</h1>
        <p className="text-sm text-muted-foreground">Every proxy call routed through your client.</p>
      </header>

      <Card>
        <CardContent className="p-0">
          {isError ? (
            <div className="p-6">
              <CardTitle className="text-destructive">Could not load transactions</CardTitle>
              <CardDescription>{(error as Error)?.message ?? 'Unknown error'}</CardDescription>
            </div>
          ) : isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No transactions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3 font-medium">When</th>
                    <th className="p-3 font-medium">Endpoint</th>
                    <th className="p-3 font-medium">Merchant</th>
                    <th className="p-3 font-medium">Store</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Latency</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map((t) => (
                    <tr key={t.id} className="hover:bg-muted/30">
                      <td className="p-3 text-xs text-muted-foreground">
                        {new Date(t.createdAt).toLocaleString()}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {t.method} {t.endpoint}
                      </td>
                      <td className="p-3 font-mono text-xs">{t.merchantId || '—'}</td>
                      <td className="p-3 font-mono text-xs">{t.storeId || '—'}</td>
                      <td className="p-3">
                        <StatusBadge code={t.statusCode} />
                      </td>
                      <td className="p-3 tabular-nums text-xs">{t.durationMs}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function StatusBadge({ code }: { code: number | string }) {
  const n = typeof code === 'number' ? code : Number(code);
  if (n >= 200 && n < 300) return <Badge variant="success">{n}</Badge>;
  if (n >= 400 && n < 500) return <Badge variant="warning">{n}</Badge>;
  if (n >= 500) return <Badge variant="destructive">{n}</Badge>;
  return <Badge variant="secondary">{n || '—'}</Badge>;
}
